import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FaChevronRight, FaLightbulb, FaInfoCircle, FaCog, FaCalculator, FaComments, FaDesktop, FaMoon, FaSun, FaRegClock, FaUserCircle, FaHistory, FaCalendarDay, FaCalendarAlt, FaMoneyBillWave, FaCheck, FaCheckCircle, FaSave, FaCopy, FaRocket } from "react-icons/fa";
import Tooltip from "../components/Tooltip";

const Tutorial = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const location = useLocation();

  // Reset and replay animations on component mount and when route changes
  useEffect(() => {
    // Reset animation state first
    setIsLoaded(false);

    // Small delay to ensure reset takes effect before starting animations
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 50);

    return () => clearTimeout(timer);
  }, [location.pathname]); // Re-run effect when the path changes

  // Tutorial steps data
  const tutorialSteps = [
    {
      title: "Welcome to Your Dashboard",
      description: "This tutorial will guide you through the main features of your dashboard. Navigate through the steps using the buttons below.",
      icon: <FaLightbulb className="text-yellow-600 dark:text-yellow-300 text-4xl" />,
    },
    {
      title: "Home Dashboard",
      description: "The home page provides a quick overview of your recent activity and quick access to important tools.",
      icon: <FaInfoCircle className="text-yellow-600 dark:text-yellow-300 text-4xl" />,
    },
    {
      title: "Rebate Calculator",
      description: "Use the rebate calculator to quickly compute customer rebates for service interruptions. Input billing cycle information, dates, and plan details to generate accurate rebate amounts.",
      icon: <FaCalculator className="text-yellow-600 dark:text-yellow-300 text-4xl" />,
    },
    {
      title: "Agent Assist",
      description: "The Agent Assist tool provides AI-powered chat support to help you quickly respond to customer inquiries. You can save and reference previous conversations for consistency.",
      icon: <FaComments className="text-yellow-600 dark:text-yellow-300 text-4xl" />,
    },
    {
      title: "Settings",
      description: "Customize your experience by adjusting settings like dark mode. More personalization options will be added in future updates.",
      icon: <FaCog className="text-yellow-600 dark:text-yellow-300 text-4xl" />,
    }
  ];

  const nextStep = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Improved Illustration Components for each step
  const renderIllustration = () => {
    switch (currentStep) {
      case 0: // Welcome
        return (
          <div className="relative bg-gray-100 dark:bg-gray-700 rounded-lg w-full h-full flex items-center justify-center overflow-hidden shadow-md">
            <div className="w-full h-full relative p-2">
              {/* Sidebar navigation */}
              <Tooltip content="Sidebar navigation menu" position="right">
                <div className="absolute left-2 top-2 bottom-2 w-14 bg-yellow-500 dark:bg-yellow-600 rounded-lg opacity-80 hover:opacity-100 hover:ring-2 hover:ring-yellow-400 dark:hover:ring-yellow-500 transition-all duration-200 cursor-help"></div>
              </Tooltip>

              {/* Application header */}
              <Tooltip content="Application header with user profile" position="bottom">
                <div className="absolute left-20 right-2 top-2 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center px-3 hover:ring-2 hover:ring-yellow-400 dark:hover:ring-yellow-500 transition-all duration-200 cursor-help">
                  <div className="flex items-center">
                    <FaUserCircle className="text-gray-500 dark:text-gray-300 text-xl mr-2" />
                    <div className="w-20 h-4 bg-gray-300 dark:bg-gray-500 rounded"></div>
                  </div>
                </div>
              </Tooltip>

              {/* Main content area */}
              <Tooltip content="Main dashboard content area" position="left">
                <div className="absolute left-20 right-2 top-16 bottom-2 bg-white dark:bg-gray-800 rounded-lg p-3 hover:ring-2 hover:ring-yellow-400 dark:hover:ring-yellow-500 transition-all duration-200 cursor-help">
                  <div className="flex justify-between items-center mb-3">
                    <div className="w-32 h-5 bg-gray-200 dark:bg-gray-600 rounded"></div>
                    <div className="flex gap-2">
                      <div className="w-20 h-5 bg-yellow-500 dark:bg-yellow-600 rounded"></div>
                      <div className="w-20 h-5 bg-gray-300 dark:bg-gray-500 rounded"></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 1, title: "Rebate Calculator" },
                      { id: 2, title: "Settings" },
                      { id: 3, title: "Agent Assist" }
                    ].map((item) => (
                      <Tooltip key={item.id} content={`Quick access to ${item.title}`} position="top">
                        <div className="h-16 bg-gray-200 dark:bg-gray-600 rounded p-2 hover:bg-gray-50 dark:hover:bg-gray-700 hover:ring-1 hover:ring-yellow-400 dark:hover:ring-yellow-500 transition-all duration-200 cursor-help">
                          <div className="w-8 h-8 bg-yellow-200 dark:bg-yellow-700 rounded-full mb-1"></div>
                          <div className="w-full h-2 bg-gray-300 dark:bg-gray-500 rounded"></div>
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              </Tooltip>

              <div className="absolute right-3 bottom-2 text-xs text-gray-500 dark:text-gray-400">
                Dashboard Overview
              </div>
            </div>
          </div>
        );

      case 1: // Home Dashboard
        return (
          <div className="relative bg-gray-100 dark:bg-gray-700 rounded-lg w-full h-full flex flex-col overflow-hidden shadow-md">
            {/* Main content area with scroll */}
            <div className="absolute inset-0 overflow-y-auto p-2 pb-6">
              {/* Welcome Card */}
              <Tooltip content="Personalized welcome card that greets you based on time of day" position="bottom">
                <div className="w-full bg-white dark:bg-gray-800 rounded-lg p-3 flex mb-3 hover:ring-2 hover:ring-yellow-400 dark:hover:ring-yellow-500 transition-all duration-200 cursor-help">
                  <div className="flex-grow">
                    <div className="flex items-center mb-2">
                      <FaRegClock className="text-yellow-500 dark:text-yellow-600 text-base mr-2" />
                      <div className="w-28 h-4 bg-gray-300 dark:bg-gray-500 rounded"></div>
                    </div>
                    <div className="w-40 h-3 bg-gray-200 dark:bg-gray-600 rounded mb-2"></div>
                    <div className="flex">
                      <div className="w-16 h-5 bg-black dark:bg-yellow-500 rounded mr-2"></div>
                      <div className="w-20 h-5 bg-gray-300 dark:bg-gray-500 rounded"></div>
                    </div>
                  </div>
                  <div className="w-1/4 bg-gradient-to-br from-yellow-400 to-yellow-600 dark:from-yellow-600 dark:to-yellow-800 rounded"></div>
                </div>
              </Tooltip>

              {/* Quick Access Title */}
              <div className="w-24 my-2 ml-2 h-4 bg-gray-300 dark:bg-gray-500 rounded"></div>

              {/* Quick Access Cards */}
              <div className="grid grid-cols-3 gap-3 mb-3">
                {[{ id: 1, title: "Rebate Calculator" }, { id: 2, title: "Settings" }, { id: 3, title: "Agent Assist" }].map((item) => (
                  <Tooltip key={item.id} content={`Quick access to ${item.title}`} position="top">
                    <div className="bg-white dark:bg-gray-800 rounded p-3 flex flex-col hover:bg-gray-50 dark:hover:bg-gray-700 hover:ring-1 hover:ring-yellow-400 dark:hover:ring-yellow-500 transition-all duration-200 cursor-help">
                      <div className="flex items-center mb-2">
                        <div className="w-6 h-6 bg-yellow-200 dark:bg-yellow-700 rounded-full mr-2"></div>
                        <div className="w-16 h-3 bg-gray-300 dark:bg-gray-500 rounded"></div>
                      </div>
                      <div className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded mb-1"></div>
                      <div className="w-12 h-3 bg-yellow-500 dark:bg-yellow-600 rounded self-end"></div>
                    </div>
                  </Tooltip>
                ))}
              </div>

              {/* Recent Activity */}
              <Tooltip content="View your latest activities and transactions" position="top">
                <div className="w-full bg-white dark:bg-gray-800 rounded-lg p-3 hover:ring-2 hover:ring-yellow-400 dark:hover:ring-yellow-500 transition-all duration-200 cursor-help">
                  <div className="w-24 h-4 bg-gray-300 dark:bg-gray-500 rounded mb-2"></div>
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-yellow-200 dark:bg-yellow-700 rounded-full mr-2"></div>
                    <div className="flex-grow">
                      <div className="w-3/4 h-2 bg-gray-200 dark:bg-gray-600 rounded mb-1"></div>
                      <div className="w-1/2 h-2 bg-gray-200 dark:bg-gray-600 rounded"></div>
                    </div>
                    <div className="text-xs dark:text-white text-gray-600">Dashboard</div>
                  </div>
                </div>
              </Tooltip>

              {/* Additional content to demonstrate scrolling */}

            </div>

            {/* Fixed label at the bottom */}
            <div className="absolute right-3 bottom-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1">
              Home Dashboard
            </div>
          </div>
        );

      case 2: // Rebate Calculator
        return (
          <div className="relative bg-gray-100 dark:bg-gray-700 rounded-lg w-full h-full flex items-center justify-center overflow-hidden shadow-md">
            <div className="w-full h-full relative p-2">
              {/* Left column - Form */}
              <Tooltip content="Input form for rebate calculations" position="right">
                <div className="absolute left-2 top-2 bottom-2 w-1/3 bg-white dark:bg-gray-800 rounded-lg p-3 hover:ring-2 hover:ring-yellow-400 dark:hover:ring-yellow-500 transition-all duration-200 cursor-help overflow-auto">
                  <div className="w-full h-4 bg-gray-300 dark:bg-gray-500 rounded mb-3"></div>

                  {/* Form fields */}
                  <div className="mb-2">
                    <div className="w-16 h-3 bg-gray-200 dark:bg-gray-600 rounded mb-1"></div>
                    <div className="w-full h-6 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600"></div>
                  </div>

                  <div className="mb-2">
                    <div className="w-16 h-3 bg-gray-200 dark:bg-gray-600 rounded mb-1"></div>
                    <div className="w-full h-6 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600"></div>
                  </div>

                  <div className="mb-2">
                    <div className="w-16 h-3 bg-gray-200 dark:bg-gray-600 rounded mb-1"></div>
                    <div className="w-full h-6 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600"></div>
                  </div>

                  <div className="mb-2">
                    <div className="w-16 h-3 bg-gray-200 dark:bg-gray-600 rounded mb-1"></div>
                    <div className="w-full h-6 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600"></div>
                  </div>

                  <div className="flex justify-center mt-3">
                    <Tooltip content="Add billing period to calculation" position="top">
                      <div className="w-20 h-6 bg-black dark:bg-yellow-500 rounded hover:opacity-80 transition-all duration-200 cursor-help"></div>
                    </Tooltip>
                  </div>
                </div>
              </Tooltip>

              {/* Right side - Table */}
              <Tooltip content="Summary of rebate calculations" position="left">
                <div className="absolute left-[calc(33%+12px)] right-2 top-2 bottom-2 ml-2 bg-white dark:bg-gray-800 rounded-lg overflow-hidden hover:ring-2 hover:ring-yellow-400 dark:hover:ring-yellow-500 transition-all duration-200 cursor-help flex flex-col">
                  {/* Table header */}
                  <div className="w-full h-8 bg-gray-200 dark:bg-gray-700 flex p-1 mb-1">
                    <div className="flex-1 h-full flex items-center">
                      <div className="w-full h-3 bg-gray-400 dark:bg-gray-500 rounded"></div>
                    </div>
                    <div className="flex-1 h-full flex items-center">
                      <div className="w-full h-3 bg-gray-400 dark:bg-gray-500 rounded"></div>
                    </div>
                    <div className="flex-1 h-full flex items-center">
                      <div className="w-full h-3 bg-gray-400 dark:bg-gray-500 rounded"></div>
                    </div>
                    <div className="flex-1 h-full flex items-center">
                      <div className="w-full h-3 bg-gray-400 dark:bg-gray-500 rounded"></div>
                    </div>
                  </div>

                  {/* Table rows */}
                  <div className="flex-1 overflow-auto">
                    {[1, 2, 3].map((item) => (
                      <div
                        key={item}
                        className="w-full h-7 flex p-1 mb-1 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
                      >
                        <div className="flex-1 h-full flex items-center">
                          <div className="w-full h-3 bg-gray-200 dark:bg-gray-600 rounded"></div>
                        </div>
                        <div className="flex-1 h-full flex items-center">
                          <div className="w-full h-3 bg-gray-200 dark:bg-gray-600 rounded"></div>
                        </div>
                        <div className="flex-1 h-full flex items-center">
                          <div className="w-full h-3 bg-gray-200 dark:bg-gray-600 rounded"></div>
                        </div>
                        <div className="flex-1 h-full flex items-center">
                          <div className="w-full h-3 bg-gray-200 dark:bg-gray-600 rounded"></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Bottom action buttons */}
                  <div className="p-2 flex justify-end">
                    <Tooltip content="Go back to previous step" position="top">
                      <div className="w-14 h-5 bg-gray-300 dark:bg-gray-600 rounded mr-2 hover:opacity-80 transition-all duration-200 cursor-help"></div>
                    </Tooltip>
                    <Tooltip content="Proceed to next step" position="top">
                      <div className="w-14 h-5 bg-yellow-500 dark:bg-yellow-600 rounded hover:opacity-80 transition-all duration-200 cursor-help"></div>
                    </Tooltip>
                  </div>
                </div>
              </Tooltip>

              <div className="absolute right-3 bottom-2 text-xs text-gray-500 dark:text-gray-400">
                Rebate Calculator
              </div>
            </div>
          </div>
        );

      case 3: // Agent Assist
        return (
          <div className="relative bg-gray-100 dark:bg-gray-700 rounded-lg w-full h-full overflow-hidden shadow-md">
            {/* Chat tabs */}
            <div className="absolute left-2 right-2 top-2 h-7 flex">
              <Tooltip content="Chat Assistant tab" position="bottom">
                <div className="w-32 h-full bg-white dark:bg-yellow-600 rounded-t-lg border-b-2 border-yellow-500 dark:border-black flex items-center justify-center hover:opacity-80 transition-all duration-200 cursor-help">
                  <FaComments className="text-yellow-500 dark:text-black text-xs mr-1" />
                  <div className="w-16 h-3 bg-gray-300 dark:bg-gray-800 rounded"></div>
                </div>
              </Tooltip>
              <Tooltip content="Email Transaction Handler tab" position="bottom">
                <div className="w-32 h-full bg-gray-200 dark:bg-gray-600 rounded-t-lg ml-1 flex items-center justify-center hover:opacity-80 transition-all duration-200 cursor-help">
                  <div className="w-20 h-3 bg-gray-400 dark:bg-gray-500 rounded"></div>
                </div>
              </Tooltip>
            </div>

            {/* Two column layout with flex */}
            <div className="absolute inset-0 pt-9 pb-2 px-2 flex gap-2">
              {/* Left column - Chat container */}
              <div className="w-7/12 h-full">
                <Tooltip content="Chat interface for customer support" position="right">
                  <div className="w-full h-full bg-white dark:bg-gray-800 rounded-lg p-2 border-t-4 border-black dark:border-yellow-500 hover:ring-1 hover:ring-yellow-400 dark:hover:ring-yellow-500 transition-all duration-200 cursor-help relative">
                    {/* Chat header */}
                    <div className="flex justify-between mb-2 h-6 border-b border-gray-200 dark:border-gray-700">
                      <div className="w-16 h-4 bg-gray-300 dark:bg-gray-500 rounded"></div>
                      <Tooltip content="Start a new chat" position="bottom">
                        <div className="w-16 h-4 bg-black dark:bg-yellow-500 rounded hover:opacity-80 transition-all duration-200 cursor-help"></div>
                      </Tooltip>
                    </div>

                    {/* Chat messages */}
                    <div className="h-[calc(100%-40px)] overflow-y-auto">
                      {/* User message */}
                      <div className="flex justify-end mb-2">
                        <Tooltip content="Your message" position="left">
                          <div className="max-w-[80%] bg-yellow-100 dark:bg-gray-600 rounded-lg p-2 hover:bg-yellow-50 dark:hover:bg-gray-500 transition-all duration-200 cursor-help">
                            <div className="w-16 h-2 bg-gray-400 dark:bg-gray-500 rounded mb-1"></div>
                            <div className="w-24 h-2 bg-gray-400 dark:bg-gray-500 rounded"></div>
                          </div>
                        </Tooltip>
                      </div>

                      {/* Bot message */}
                      <div className="flex justify-start mb-2">
                        <Tooltip content="AI assistant response" position="right">
                          <div className="max-w-[80%] bg-gray-100 dark:bg-gray-700 rounded-lg p-2 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 cursor-help">
                            <div className="w-32 h-2 bg-gray-400 dark:bg-gray-500 rounded mb-1"></div>
                            <div className="w-40 h-2 bg-gray-400 dark:bg-gray-500 rounded mb-1"></div>
                            <div className="w-24 h-2 bg-gray-400 dark:bg-gray-500 rounded"></div>
                          </div>
                        </Tooltip>
                      </div>

                      {/* User message */}
                      <div className="flex justify-end">
                        <Tooltip content="Your message" position="left">
                          <div className="max-w-[80%] bg-yellow-100 dark:bg-gray-600 rounded-lg p-2 hover:bg-yellow-50 dark:hover:bg-gray-500 transition-all duration-200 cursor-help">
                            <div className="w-28 h-2 bg-gray-400 dark:bg-gray-500 rounded"></div>
                          </div>
                        </Tooltip>
                      </div>
                    </div>

                    {/* Input box */}
                    <Tooltip content="Type your message here" position="top">
                      <div className="absolute bottom-2 left-2 right-2 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex hover:ring-1 hover:ring-yellow-400 dark:hover:ring-yellow-500 transition-all duration-200 cursor-help">
                        <div className="flex-grow"></div>
                        <Tooltip content="Send message" position="left">
                          <div className="w-8 h-full bg-black dark:bg-yellow-500 rounded-r-lg flex items-center justify-center hover:opacity-80 transition-all duration-200 cursor-help">
                            <FaChevronRight className="text-white dark:text-black text-xs" />
                          </div>
                        </Tooltip>
                      </div>
                    </Tooltip>
                  </div>
                </Tooltip>
              </div>

              {/* Right column - Saved chats panel */}
              <div className="w-5/12 h-full">
                <Tooltip content="Saved chat history" position="left">
                  <div className="w-full h-full bg-white dark:bg-gray-800 rounded-lg p-2 border-t-4 border-black dark:border-yellow-500 hover:ring-1 hover:ring-yellow-400 dark:hover:ring-yellow-500 transition-all duration-200 cursor-help">
                    <div className="w-20 h-4 bg-gray-300 dark:bg-gray-500 rounded mb-3"></div>

                    <Tooltip content="Previously saved chat" position="left">
                      <div className="w-full h-12 bg-gray-100 dark:bg-gray-700 rounded-lg p-2 mb-2 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 cursor-help">
                        <div className="w-24 h-3 bg-gray-300 dark:bg-gray-500 rounded mb-1"></div>
                        <div className="w-16 h-2 bg-gray-400 dark:bg-gray-600 rounded"></div>
                      </div>
                    </Tooltip>

                    <Tooltip content="Previously saved chat" position="left">
                      <div className="w-full h-12 bg-gray-100 dark:bg-gray-700 rounded-lg p-2 mb-2 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 cursor-help">
                        <div className="w-24 h-3 bg-gray-300 dark:bg-gray-500 rounded mb-1"></div>
                        <div className="w-16 h-2 bg-gray-400 dark:bg-gray-600 rounded"></div>
                      </div>
                    </Tooltip>
                  </div>
                </Tooltip>
              </div>
            </div>

            <div className="absolute right-3 bottom-2 text-xs text-gray-500 dark:text-gray-400">
              Agent Assist
            </div>
          </div>
        );

      case 4: // Settings
        return (
          <div className="relative bg-gray-100 dark:bg-gray-700 rounded-lg w-full h-full overflow-hidden shadow-md">
            <Tooltip content="Settings header" position="bottom">
              <div className="absolute left-2 right-2 top-2 h-8 flex items-center hover:bg-gray-50 dark:hover:bg-gray-600 rounded px-2 transition-all duration-200 cursor-help">
                <FaCog className="text-gray-500 dark:text-gray-300 text-base mr-2" />
                <div className="w-24 h-5 bg-gray-300 dark:bg-gray-500 rounded"></div>
              </div>
            </Tooltip>

            {/* Dark mode toggle */}
            <Tooltip content="Toggle between light and dark mode" position="right">
              <div className="absolute left-2 right-2 top-12 h-10 flex items-center px-2 hover:bg-gray-50 dark:hover:bg-gray-600 rounded transition-all duration-200 cursor-help">
                <div className="w-10 h-6 bg-yellow-500 dark:bg-yellow-600 rounded-full relative">
                  <div className="w-4 h-4 bg-white rounded-full absolute right-1 top-1"></div>
                </div>
                <div className="w-32 h-4 bg-gray-300 dark:bg-gray-500 rounded ml-3"></div>
              </div>
            </Tooltip>

            {/* Settings cards - light vs dark mode illustration */}
            <div className="absolute inset-0 pt-24 pb-8 px-2 flex gap-2">
              <div className="w-1/2">
                <Tooltip content="Light mode appearance" position="right">
                  <div className="w-full h-full bg-white rounded-lg p-2 overflow-hidden hover:ring-2 hover:ring-yellow-400 transition-all duration-200 cursor-help">
                    <div className="w-full h-7 flex items-center mb-2">
                      <FaSun className="text-yellow-500 text-lg mr-2" />
                      <div className="w-full h-4 bg-gray-300 rounded"></div>
                    </div>

                    <div className="w-full h-full bg-white p-2">
                      <div className="w-full h-8 bg-gray-200 rounded mb-2 flex items-center p-2">
                        <div className="w-8 h-4 bg-gray-300 rounded mr-2"></div>
                        <div className="w-2/3 h-4 bg-gray-300 rounded"></div>
                      </div>
                      <div className="w-full h-8 bg-gray-200 rounded flex items-center p-2">
                        <div className="w-8 h-4 bg-gray-300 rounded mr-2"></div>
                        <div className="w-2/3 h-4 bg-gray-300 rounded"></div>
                      </div>
                    </div>
                  </div>
                </Tooltip>
              </div>

              <div className="w-1/2">
                <Tooltip content="Dark mode appearance" position="left">
                  <div className="w-full h-full bg-gray-800 rounded-lg p-2 overflow-hidden hover:ring-2 hover:ring-yellow-500 transition-all duration-200 cursor-help">
                    <div className="w-full h-7 flex items-center mb-2">
                      <FaMoon className="text-yellow-400 text-lg mr-2" />
                      <div className="w-full h-4 bg-gray-600 rounded"></div>
                    </div>

                    <div className="w-full h-full bg-gray-800 p-2">
                      <div className="w-full h-8 bg-gray-700 rounded mb-2 flex items-center p-2">
                        <div className="w-8 h-4 bg-gray-600 rounded mr-2"></div>
                        <div className="w-2/3 h-4 bg-gray-600 rounded"></div>
                      </div>
                      <div className="w-full h-8 bg-gray-700 rounded flex items-center p-2">
                        <div className="w-8 h-4 bg-gray-600 rounded mr-2"></div>
                        <div className="w-2/3 h-4 bg-gray-600 rounded"></div>
                      </div>
                    </div>
                  </div>
                </Tooltip>
              </div>
            </div>

            <div className="absolute right-3 bottom-2 text-xs text-gray-500 dark:text-gray-400">
              Settings
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      <div className="flex-grow overflow-y-auto px-2 md:px-4 py-3">
        <div className={`bg-white bg-opacity-60 dark:bg-white dark:bg-opacity-10 rounded-lg p-4 h-full flex flex-col transition-all duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
          {/* Tutorial Header */}
          <div className={`transition-all duration-500 delay-100 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Tutorial</h2>
            <p className="text-gray-600 dark:text-gray-300 text-lg mb-6">Learn how to use the Agent Tool Hub effectively</p>
          </div>

          {/* Progress Bar */}
          <div className={`w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full mb-6 transition-all duration-500 delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div
              className="bg-yellow-600 dark:bg-uasYellow h-2 rounded-full transition-all duration-300 ease-in-out"
              style={{ width: `${(currentStep / (tutorialSteps.length - 1)) * 100}%` }}
            ></div>
          </div>

          {/* Content Container - Fixed to prevent overflow */}
          <div className="bg-white dark:bg-laryzeDarkGray dark:bg-opacity-90 rounded-lg shadow-lg p-4 md:p-6 mb-6 border-t-8 border-t-black dark:border-t-uasYellow transition-all duration-500 delay-300 flex-grow flex flex-col overflow-hidden">            <div className="flex flex-col md:flex-row items-center mb-4 md:mb-6">
            <div className="rounded-full bg-yellow-100 dark:bg-yellow-900 p-4 md:p-5 mb-4 md:mb-0 md:mr-6">
              {tutorialSteps[currentStep].icon}
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                {tutorialSteps[currentStep].title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {tutorialSteps[currentStep].description}
              </p>
            </div>
          </div>

            {/* Two-column layout that stacks on mobile - with max height constraint */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 flex-grow overflow-hidden">
              {/* Left side: Illustration - with controlled height */}
              <div className={`transition-all duration-500 delay-400 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg shadow-md overflow-hidden h-full max-h-64 md:max-h-full">
                  {renderIllustration()}
                </div>
              </div>

              {/* Right side: Step content - with overflow scroll if needed */}
              <div className="bg-gray-50 dark:bg-gray-800 dark:bg-opacity-50 rounded-lg p-3 md:p-4 h-full max-h-64 md:max-h-full overflow-y-auto">
                {currentStep === 0 && (
                  <div className={`transition-all duration-500 delay-450 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                    <h4 className="font-medium text-gray-800 dark:text-white mb-2">Getting Started</h4>
                    <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Navigate using the sidebar menu on the left</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Toggle between light and dark mode in Settings</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Access quick tools from the dashboard</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>View your recent activity in the activity section</span>
                      </li>
                    </ul>
                  </div>
                )}

                {currentStep === 1 && (
                  <div className={`transition-all duration-500 delay-450 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                    <h4 className="font-medium text-gray-800 dark:text-white mb-2">Dashboard Features</h4>
                    <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>The welcome card shows personalized greetings based on time of day</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Quick access cards let you jump to frequently used tools</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Recent activity shows your latest actions for easy tracking</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>View all activity to see your complete history</span>
                      </li>
                    </ul>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className={`transition-all duration-500 delay-450 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                    <h4 className="font-medium text-gray-800 dark:text-white mb-2">Using the Rebate Calculator</h4>
                    <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Select the appropriate billing cycle from the dropdown menu</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Input the start and end dates of the service interruption</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Enter the customer's monthly service fee (MSF)</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Add multiple billing periods if needed and review the summary</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Complete the CARE format and submit for approval</span>
                      </li>
                    </ul>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className={`transition-all duration-500 delay-450 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                    <h4 className="font-medium text-gray-800 dark:text-white mb-2">Agent Assist Chat Features</h4>
                    <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Use the chatbot to get quick answers to customer inquiries</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Your chat history is saved automatically for future reference</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Copy or save important conversations with one click</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Start a new chat when working with a different customer</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>View all saved chats in the right panel for easy access</span>
                      </li>
                    </ul>
                  </div>
                )}

                {currentStep === 4 && (
                  <div className={`transition-all duration-500 delay-450 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                    <h4 className="font-medium text-gray-800 dark:text-white mb-2">Personalize Your Experience</h4>
                    <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Toggle between light and dark mode based on your preference</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Dark mode reduces eye strain in low-light environments</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Your settings are saved automatically between sessions</span>
                      </li>
                      <li className="flex items-start">
                        <FaChevronRight className="text-yellow-600 dark:text-yellow-300 mt-1 mr-2 flex-shrink-0" />
                        <span>Additional personalization options will be added soon</span>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className={`flex justify-between transition-all duration-500 delay-550 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`px-6 py-2.5 ${currentStep === 0
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600'
                } font-medium text-sm rounded-lg focus:ring-4 focus:ring-gray-200 dark:focus:ring-gray-700 transition-all`}
            >
              Previous
            </button>

            <button
              onClick={nextStep}
              disabled={currentStep === tutorialSteps.length - 1}
              className={`px-6 py-2.5 ${currentStep === tutorialSteps.length - 1
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-gray-900 text-white dark:bg-uasYellow dark:text-black hover:bg-uasYellow hover:text-white dark:hover:bg-black dark:hover:text-white'
                } font-medium text-sm rounded-lg focus:ring-4 focus:ring-yellow-300 transition-all`}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tutorial;